import type { SluggedId } from "Models";
import { leaguesV2Api } from "../leaguesV2Api";
import {
  addOptimisticMember,
  applyMemberEdit,
  removeMemberById,
  removeMemberByUserId,
  replaceOptimisticMember,
  restoreMember,
  type MemberEdit,
} from "../optimistic";
import { toRosterTeeType } from "../../components/helpers";
import type {
  Clubhouse_LeagueV2RosterResponseDto,
  Clubhouse_LeagueV2RosterMemberDto,
  Clubhouse_PostLeagueV2RosterMemberDto,
  Clubhouse_PatchLeagueV2RosterMember,
  Clubhouse_LeagueV2PhoneSearchResponseDto,
  Clubhouse_LeagueRosterImportResultDto,
  Clubhouse_PostLeagueV2RosterCreateUserDto,
  Clubhouse_PostLeagueV2RosterCreateUserResponseDto,
  Clubhouse_UserSearchPlayerDto,
  Clubhouse_LeagueV2PhoneSearchUserDto,
} from "../../types/leagueRoster";

type GetLeagueRosterArgs = {
  facilityId: string;
  leagueId: string;
};

type SearchFacilityUsersArgs = {
  facilityId: string;
  query: string;
};

type SearchLockerUsernameArgs = {
  username: string;
};

type AddRosterMemberArgs = {
  facilityId: string;
  leagueId: string;
  body: Clubhouse_PostLeagueV2RosterMemberDto;
};

type ImportRosterArgs = {
  facilityId: string;
  leagueId: string;
  body: FormData;
};

type CreateRosterMemberArgs = {
  facilityId: string;
  leagueId: string;
  body: Clubhouse_PostLeagueV2RosterCreateUserDto;
};

type PatchRosterMemberArgs = {
  facilityId: string;
  leagueId: string;
  memberId: SluggedId;
  body: Clubhouse_PatchLeagueV2RosterMember;
};

type DeleteRosterMemberArgs = {
  facilityId: string;
  leagueId: string;
  memberId: SluggedId;
};

type DeleteRosterMemberByUserIdArgs = {
  facilityId: string;
  leagueId: string;
  userId: SluggedId;
};

/**
 * Mutations that can change who is on a team, not just who is on the roster.
 *
 * Removing a member takes them off their team; a patch can do the same
 * indirectly, since flipping someone to substitute-only or inactive takes them
 * out of the full-time pool the Teams board lays out. The board reads the teams
 * payload as well as the roster, so both have to be refetched or it keeps
 * rendering whatever it fetched before the write.
 *
 * Note this does not by itself clear a removed player off a team card. Removal
 * is a soft delete and GET /teams carries no active flag on its player rows, so
 * it keeps returning them and a refetch returns the same player. That is
 * ticketed against the API. This is what lets the board pick the correction up
 * without a reload once it lands.
 */
const ROSTER_AND_TEAMS = ["LeagueRoster", "LeagueTeam"] as const;

/**
 * Round-scoped reads — the substitute search and the rounds list — are
 * tagged `{ type: "League", id: leagueId }`, not "LeagueRoster". Every roster
 * write (add/edit/remove/import) must invalidate that too, or a player's
 * updated isSubstituteOnly flag (or a removal) leaves a round's substitute
 * rail stale until a hard refresh.
 */
const leagueTag = (leagueId: string) => [
  { type: "League" as const, id: leagueId },
];

const rosterAndLeagueTags = (leagueId: string) => [
  "LeagueRoster" as const,
  ...leagueTag(leagueId),
];

const rosterTeamsAndLeagueTags = (leagueId: string) => [
  ...ROSTER_AND_TEAMS,
  ...leagueTag(leagueId),
];

/**
 * Queries first, in an injection of their own.
 *
 * The mutations below update this cache by hand, and `updateQueryData` types
 * its endpoint name against the api object it is called on — so it has to be
 * called on an api that already knows `getLeagueRoster` exists. The base api
 * is created with no endpoints, so naming one there resolves to `never` and
 * takes the draft's type down with it. Splitting the injection gives the
 * mutations a fully-resolved api to point at, with no circular inference.
 */
const rosterQueries = leaguesV2Api.injectEndpoints({
  endpoints: (build) => ({
    getLeagueRoster: build.query<
      Clubhouse_LeagueV2RosterResponseDto,
      GetLeagueRosterArgs
    >({
      query: ({ facilityId, leagueId }) =>
        `/api/facility/${facilityId}/leagues/${leagueId}/roster`,
      // The tee is normalized here rather than at each consumer, so the DTO's
      // declared `teeType` is true by the time anything reads it. This field
      // has already shipped in a shape the type denied once — a numeric value
      // reaching the label formatter throws, and from a table cell that takes
      // down the whole roster rather than one dialog.
      // Anything that isn't a list passes through untouched rather than being
      // coerced to []: a malformed response should surface as one, not as an
      // empty roster — the operator would read that as "no players".
      transformResponse: (rows: Clubhouse_LeagueV2RosterResponseDto) =>
        Array.isArray(rows)
          ? rows.map((member) => ({
              ...member,
              teeType: toRosterTeeType(member.teeType) ?? undefined,
            }))
          : rows,
      providesTags: ["LeagueRoster"],
    }),

    searchFacilityUsers: build.query<
      Clubhouse_LeagueV2PhoneSearchResponseDto,
      SearchFacilityUsersArgs
    >({
      query: ({ facilityId, query }) =>
        `/api/facility/${facilityId}/users/search?q=${encodeURIComponent(query)}`,
      // Same normalization as the roster: this is the endpoint whose tee
      // actually arrived name-shaped against a numeric declaration.
      transformResponse: (res: Clubhouse_LeagueV2PhoneSearchResponseDto) => {
        const fix = (u: Clubhouse_LeagueV2PhoneSearchUserDto) => ({
          ...u,
          teeType: toRosterTeeType(u.teeType) ?? undefined,
        });
        return {
          ...res,
          match: Array.isArray(res.match)
            ? res.match.map(fix)
            : res.match && fix(res.match),
          matches: res.matches?.map(fix),
        };
      },
    }),

    searchLockerUsername: build.query<
      Clubhouse_UserSearchPlayerDto[],
      SearchLockerUsernameArgs
    >({
      query: ({ username }) => ({
        url: `/api/players/search?names=${encodeURIComponent(username)}`,
        method: "GET",
      }),
    }),
  }),
});

const rosterEndpoints = rosterQueries.injectEndpoints({
  endpoints: (build) => ({
    addRosterMember: build.mutation<
      Clubhouse_LeagueV2RosterMemberDto,
      AddRosterMemberArgs
    >({
      query: ({ facilityId, leagueId, body }) => ({
        url: `/api/facility/${facilityId}/leagues/${leagueId}/roster`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, { leagueId }) => rosterAndLeagueTags(leagueId),
      // The response already carries everything the roster list needs — name,
      // handicap, substitute flag — bar the id the server assigns, so there's
      // no reason to wait on the follow-up refetch of the whole roster just to
      // see the row this write already produced. A temporary id holds the row's
      // place until the real one lands.
      async onQueryStarted(
        { facilityId, leagueId, body },
        { dispatch, queryFulfilled },
      ) {
        const tempId = `optimistic-${Date.now()}-${Math.random()}` as SluggedId;
        dispatch(
          rosterQueries.util.updateQueryData(
            "getLeagueRoster",
            { facilityId, leagueId },
            (draft) =>
              addOptimisticMember(draft, {
                memberId: tempId,
                userId: body.userId,
                playerName: body.playerName,
                teeType: toRosterTeeType(body.teeSetId) ?? undefined,
                seedHandicap: body.seedHandicap,
                currentHandicap: body.currentHandicap,
                handicapStatus: body.handicapStatus,
                isSubstituteOnly: body.isSubstituteOnly,
                isActive: true,
              }),
          ),
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            rosterQueries.util.updateQueryData(
              "getLeagueRoster",
              { facilityId, leagueId },
              (draft) =>
                replaceOptimisticMember(draft, tempId, {
                  ...data,
                  teeType: toRosterTeeType(data.teeType) ?? undefined,
                }),
            ),
          );
        } catch {
          // By the placeholder id, not `patch.undo()`: this mutation
          // invalidates, so an overlapping roster write that succeeds first
          // refetches and reorders the list before this one fails, and a
          // positional undo would drop whichever member now sits at the
          // index captured when the row went in.
          dispatch(
            rosterQueries.util.updateQueryData(
              "getLeagueRoster",
              { facilityId, leagueId },
              (draft) => {
                removeMemberById(draft, tempId);
              },
            ),
          );
        }
      },
    }),

    importRoster: build.mutation<
      Clubhouse_LeagueRosterImportResultDto,
      ImportRosterArgs
    >({
      query: ({ facilityId, leagueId, body }) => ({
        url: `/api/facility/${facilityId}/leagues/${leagueId}/roster/import`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, { leagueId }) => rosterAndLeagueTags(leagueId),
    }),

    createRosterMember: build.mutation<
      Clubhouse_PostLeagueV2RosterCreateUserResponseDto,
      CreateRosterMemberArgs
    >({
      query: ({ facilityId, leagueId, body }) => ({
        url: `/api/facility/${facilityId}/leagues/${leagueId}/roster/create`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_, __, { leagueId }) => rosterAndLeagueTags(leagueId),
    }),

    patchRosterMember: build.mutation<
      Clubhouse_LeagueV2RosterMemberDto,
      PatchRosterMemberArgs
    >({
      query: ({ facilityId, leagueId, memberId, body }) => ({
        url: `/api/facility/${facilityId}/leagues/${leagueId}/roster/${memberId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_, __, { leagueId }) =>
        rosterTeamsAndLeagueTags(leagueId),
      async onQueryStarted(
        { facilityId, leagueId, memberId, body },
        { dispatch, queryFulfilled },
      ) {
        // Only the fields this write actually carries. A key the body omits
        // means "no change" and must stay out of the edit, or the optimistic
        // patch would blank it.
        const edit: MemberEdit = {};
        if (body.seedHandicap !== undefined)
          edit.seedHandicap = body.seedHandicap;
        if (body.currentHandicap !== undefined)
          edit.currentHandicap = body.currentHandicap;
        if (body.handicapStatus !== undefined)
          edit.handicapStatus = body.handicapStatus;
        if (body.isActive !== undefined) edit.isActive = body.isActive;
        if (body.isSubstituteOnly !== undefined)
          edit.isSubstituteOnly = body.isSubstituteOnly;
        // The key can be entirely absent ("no change"); `in` is the only way
        // to tell that apart from an explicit `null` ("clear it").
        if ("teeSetId" in body) {
          edit.teeType =
            body.teeSetId == null
              ? undefined
              : (toRosterTeeType(body.teeSetId) ?? undefined);
        }

        let previous: MemberEdit | undefined;
        dispatch(
          rosterQueries.util.updateQueryData(
            "getLeagueRoster",
            { facilityId, leagueId },
            (draft) => {
              previous = applyMemberEdit(draft, memberId, edit);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          if (!previous) return;
          // Re-applied by id rather than `patch.undo()`: the inverse of a
          // field assignment is a replace at an array index, and this
          // mutation invalidates, so a concurrent add or remove can reorder
          // the roster first — the undo would then write this player's old
          // handicap onto whoever now sits at that index.
          dispatch(
            rosterQueries.util.updateQueryData(
              "getLeagueRoster",
              { facilityId, leagueId },
              (draft) => {
                applyMemberEdit(draft, memberId, previous!);
              },
            ),
          );
        }
      },
    }),

    // Both deletes below reinsert the removed row on failure rather than
    // `patch.undo()`: undo replays the inverse of a positional splice
    // captured at dispatch time, which can land at the wrong index — or
    // clobber a different row entirely — once a concurrent delete on the
    // same roster has already changed the array's shape. Re-adding the one
    // row this call actually removed, by id and against whatever the list
    // looks like when the failure lands, can't do that.
    deleteRosterMember: build.mutation<void, DeleteRosterMemberArgs>({
      query: ({ facilityId, leagueId, memberId }) => ({
        url: `/api/facility/${facilityId}/leagues/${leagueId}/roster/${memberId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, { leagueId }) => [
        ...rosterTeamsAndLeagueTags(leagueId),
        "LeagueRound",
        "Standings",
      ],
      async onQueryStarted(
        { facilityId, leagueId, memberId },
        { dispatch, queryFulfilled },
      ) {
        let removedMember: Clubhouse_LeagueV2RosterMemberDto | undefined;
        dispatch(
          rosterQueries.util.updateQueryData(
            "getLeagueRoster",
            { facilityId, leagueId },
            (draft) => {
              removedMember = removeMemberById(draft, memberId);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          if (!removedMember) return;
          dispatch(
            rosterQueries.util.updateQueryData(
              "getLeagueRoster",
              { facilityId, leagueId },
              (draft) => restoreMember(draft, removedMember!),
            ),
          );
        }
      },
    }),

    deleteRosterMemberByUserId: build.mutation<
      void,
      DeleteRosterMemberByUserIdArgs
    >({
      query: ({ facilityId, leagueId, userId }) => ({
        url: `/api/facility/${facilityId}/leagues/${leagueId}/roster/users/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, { leagueId }) => [
        ...rosterTeamsAndLeagueTags(leagueId),
        "LeagueRound",
        "Standings",
      ],
      async onQueryStarted(
        { facilityId, leagueId, userId },
        { dispatch, queryFulfilled },
      ) {
        let removedMember: Clubhouse_LeagueV2RosterMemberDto | undefined;
        dispatch(
          rosterQueries.util.updateQueryData(
            "getLeagueRoster",
            { facilityId, leagueId },
            (draft) => {
              removedMember = removeMemberByUserId(draft, userId);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          if (!removedMember) return;
          dispatch(
            rosterQueries.util.updateQueryData(
              "getLeagueRoster",
              { facilityId, leagueId },
              (draft) => restoreMember(draft, removedMember!),
            ),
          );
        }
      },
    }),
  }),
});

export const {
  useGetLeagueRosterQuery,
  useSearchFacilityUsersQuery,
  useLazySearchLockerUsernameQuery,
  useAddRosterMemberMutation,
  useImportRosterMutation,
  useCreateRosterMemberMutation,
  usePatchRosterMemberMutation,
  useDeleteRosterMemberMutation,
  useDeleteRosterMemberByUserIdMutation,
} = rosterEndpoints;
